import { SplashScreen } from 'src/modules/splash-screen';
import type { PreviousReloadCover } from 'src/modules/splash-screen';
import { reportError } from 'src/modules/logs/Sentry';
import { logAnalyticsEvent as defaultLogAnalyticsEvent } from 'src/modules/analytics/analytics';
import type { AnalyticsEvents } from 'src/modules/analytics/analytics';
import { BOOT_WATCHDOG_MS } from './bootWatchdog';

// Backstop so no boot path leaves the native splash up (a cover can precede any reload): armed on
// the first line of initApp; the update gate cancels it by releasing when it needs the screen.
// Kept equal to the boot watchdog: a shorter one fires routinely on slow phones.
export const SPLASH_FALLBACK_MS = BOOT_WATCHDOG_MS;

export type SplashReleaseReason =
  'first_paint' | 'watchdog' | 'fallback' | 'gate';

export type SplashBootPath = 'camera_fast_track' | 'normal';

type Deps = {
  hide: () => Promise<unknown>;
  reportError: (message: string, error?: unknown) => void;
  logAnalyticsEvent?: (
    eventName: AnalyticsEvents,
    eventParams?: Record<string, unknown>,
  ) => unknown;
  takePreviousReloadCover?: () => PreviousReloadCover | null;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => () => void;
};

const defaultSchedule = (fn: () => void, ms: number) => {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
};

export const createSplashRelease = ({
  hide,
  reportError: report,
  // Bare name on purpose: the inventory extractor only finds bare `logAnalyticsEvent(` calls.
  logAnalyticsEvent = defaultLogAnalyticsEvent,
  takePreviousReloadCover = () => SplashScreen.takePreviousReloadCover(),
  now = () => Math.round(performance.now()),
  schedule = defaultSchedule,
}: Deps) => {
  let released = false;
  let cancelFallback: (() => void) | null = null;
  let bootPath: SplashBootPath = 'normal';
  const pendingRedirects = new Map<string, number>();

  const disarm = () => {
    cancelFallback?.();
    cancelFallback = null;
  };

  const release = (reason: SplashReleaseReason) => {
    if (released) return;
    released = true;
    disarm();
    try {
      hide().catch(error => report('Error hiding splash screen', error));
    } catch (error) {
      report('Error hiding splash screen', error);
    }
    // The reload cover that preceded this boot, if any: the old context could not report it.
    let previousCover: PreviousReloadCover | null = null;
    try {
      previousCover = takePreviousReloadCover();
    } catch {
      // Telemetry only.
    }
    // The real first-paint timing: Android WebView has no paint-timing entries.
    try {
      Promise.resolve(
        logAnalyticsEvent('splash_released', {
          releaseReason: reason,
          durationMs: now(),
          bootPath,
          ...(previousCover && {
            reloadCoverShow: previousCover.show,
            reloadCoverSource: previousCover.source,
            reloadCoverBranch: previousCover.branch,
            reloadCoverAgeMs: previousCover.ageMs,
          }),
        }),
      ).catch(() => {});
    } catch {
      // Analytics must never break the splash release.
    }
  };

  const setBootPath = (path: SplashBootPath) => {
    bootPath = path;
  };

  const armFallback = () => {
    if (released || cancelFallback) return;
    cancelFallback = schedule(() => release('fallback'), SPLASH_FALLBACK_MS);
  };

  const beginRedirect = (locationKey: string) => {
    pendingRedirects.set(
      locationKey,
      (pendingRedirects.get(locationKey) ?? 0) + 1,
    );
  };
  const endRedirect = (locationKey: string) => {
    const remaining = (pendingRedirects.get(locationKey) ?? 0) - 1;
    if (remaining > 0) pendingRedirects.set(locationKey, remaining);
    else pendingRedirects.delete(locationKey);
  };

  return {
    release,
    setBootPath,
    armFallback,
    disarm,
    isReleased: () => released,
    beginRedirect,
    endRedirect,
    isRedirectPendingAt: (locationKey: string) =>
      pendingRedirects.has(locationKey),
  };
};

export const splashRelease = createSplashRelease({
  hide: () => SplashScreen.hide(),
  reportError,
});
